import React, { useState, useEffect } from 'react';
import { Button, Alert, Modal } from 'react-bootstrap';

const AudioPermissionRequest = ({ onPermissionGranted }) => {
  const [showModal, setShowModal] = useState(false);
  const [audioContext, setAudioContext] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false);

  useEffect(() => {
    // Vérifier si l'audio est déjà autorisé
    checkAudioPermission();
  }, []);

  const checkAudioPermission = async () => {
    try {
      // Vérifier d'abord si l'utilisateur a déjà fait un choix
      const savedPreference = localStorage.getItem('audioPermissionPreference');

      if (savedPreference === 'granted') {
        // L'utilisateur a déjà accordé la permission
        setPermissionGranted(true);
        const context = new (window.AudioContext || window.webkitAudioContext)();
        if (onPermissionGranted) {
          onPermissionGranted(context);
        }
        return;
      } else if (savedPreference === 'denied') {
        // L'utilisateur a déjà refusé la permission
        setPermissionGranted(false);
        return;
      }

      // Si aucune préférence sauvegardée, essayer de créer un contexte audio
      const context = new (window.AudioContext || window.webkitAudioContext)();

      // Si le contexte est en état "suspended", demander la permission
      if (context.state === 'suspended') {
        setShowModal(true);
        setAudioContext(context);
      } else {
        setPermissionGranted(true);
        localStorage.setItem('audioPermissionPreference', 'granted');
        if (onPermissionGranted) {
          onPermissionGranted(context);
        }
      }
    } catch (error) {
      console.log("Erreur lors de la vérification de la permission audio:", error);
      setShowModal(true);
    }
  };

  const requestAudioPermission = async () => {
    try {
      if (audioContext) {
        // Demander la permission en résumant le contexte
        await audioContext.resume();

        // Tester la lecture d'un son court
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Volume très bas pour le test

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);

        setPermissionGranted(true);
        setShowModal(false);

        // Sauvegarder la préférence
        localStorage.setItem('audioPermissionPreference', 'granted');

        if (onPermissionGranted) {
          onPermissionGranted(audioContext);
        }

        console.log("Permission audio accordée avec succès");
      }
    } catch (error) {
      console.log("Erreur lors de la demande de permission audio:", error);
      // Continuer sans audio
      setPermissionGranted(false);
      setShowModal(false);
      localStorage.setItem('audioPermissionPreference', 'denied');
    }
  };

  const skipAudioPermission = () => {
    setPermissionGranted(false);
    setShowModal(false);
    // Sauvegarder la préférence
    localStorage.setItem('audioPermissionPreference', 'denied');
    console.log("Permission audio ignorée - notifications visuelles uniquement");
  };

  if (permissionGranted) {
    return null; // Ne rien afficher si la permission est accordée
  }

  return (
    <Modal show={showModal} onHide={skipAudioPermission} centered>
      <Modal.Header closeButton>
        <Modal.Title>🔔 Notifications Audio</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Pour recevoir des notifications audio lors de nouvelles commandes,
          nous avons besoin de votre permission pour jouer des sons.
        </p>
        <Alert variant="info">
          <strong>Note :</strong> Cette permission est nécessaire pour que vous
          puissiez entendre les notifications même sans interagir avec la page.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={skipAudioPermission}>
          Ignorer
        </Button>
        <Button variant="primary" onClick={requestAudioPermission}>
          Autoriser les notifications audio
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AudioPermissionRequest;